import { SubSDK } from "@/sdk/types";
import { DataStore } from "..";
import { MemoryKeys, Search } from "../types";
import Fuse from "fuse.js";
import { SDKConfiguration } from "@/inference/setup/config";
import { HeliumSDK } from "@/sdk/helium";

const BRAND_OVERRIDE = "";

const cloudfrontUrl = "https://d21xluao80us9l.cloudfront.net"; //"https://helium-intelligence-production.s3.eu-north-1.amazonaws.com";
export class DataProvider {
  static sdk: SubSDK;
  private static fuseIndex: Fuse<any> | null = null;
  static fetchMapping: Record<string, boolean> = {};
  static fetchPromises: Record<string, Promise<any>> = {};
  static isFetching({
    id,
    mode,
    key,
  }: {
    id?: string;
    mode: MemoryKeys;
    key: string;
  }) {
    const cacheKey = `${id}-${mode}-${key}`;
    return Boolean(DataProvider.fetchMapping[cacheKey]);
  }
  static setFetching({
    id,
    mode,
    key,
    promise,
  }: {
    id?: string;
    mode: MemoryKeys;
    key: string;
    promise: Promise<any>;
  }) {
    const cacheKey = `${id}-${mode}-${key}`;
    DataProvider.fetchPromises[cacheKey] = promise;
    DataProvider.fetchMapping[cacheKey] = true;
  }
  static getFetchingPromise({
    id,
    mode,
    key,
  }: {
    id?: string;
    mode: MemoryKeys;
    key: string;
  }) {
    const cacheKey = `${id}-${mode}-${key}`;
    return DataProvider.fetchPromises[cacheKey];
  }
  static heliumSDK: SubSDK;
  static nullCheck(id: string, mode: MemoryKeys = "product", key: string) {
    if (!DataStore.memory) {
      DataStore.memory = {};
    }
    if (!DataStore.memory[mode]) {
      DataStore.memory[mode] = {};
    }
    if (!DataStore.memory[mode][id]) {
      DataStore.memory[mode][id] = {};
    }
    if (!DataStore.memory[mode][key]) {
      DataStore.memory[mode][key] = {};
    }
  }
  static inCache({
    id,
    mode,
    key,
  }: {
    id?: string;
    mode: MemoryKeys;
    key: string;
  }) {
    this.nullCheck(id, mode, key);
    return id
      ? Boolean(DataStore.memory[mode][id]?.[key])
      : Boolean(DataStore.memory[mode][key]);
  }
  static cachedResult({
    id,
    mode,
    key,
  }: {
    id?: string;
    mode: MemoryKeys;
    key: string;
  }) {
    return id ? DataStore.memory[mode][id]?.[key] : DataStore.memory[mode][key];
  }
  static async cacheCheck({ id, mode }: { id: string; mode: MemoryKeys }) {
    if (DataStore.memory[mode][id]) {
      DataStore.memory[mode][id] = {};
    }
  }
  static async getTags({
    id,
    mode = "product",
  }: {
    id: string;
    mode?: MemoryKeys;
  }) {
    if (this.isFetching({ id, mode, key: "tags" }))
      return this.getFetchingPromise({ id, mode, key: "tags" });
    if (this.inCache({ id, mode, key: "tags" }))
      return this.cachedResult({ id, mode, key: "tags" });
    const promise = new Promise<any>((resolve) => {
      const tags = this.sdk.getTags({ id }).catch((e) => {
        console.log("Error getting tags", e);
        return {};
      });
      resolve(tags);
    });
    this.setFetching({ id, mode, key: "tags", promise });
    const tags = await promise;
    // only works for product right now
    // TODO: add support for other modes
    DataStore.memory[mode][id].tags = tags;
  }
  static async getFaq({
    id,
    mode = "product",
  }: {
    id: string;
    mode?: MemoryKeys;
  }) {
    if (this.inCache({ id, mode, key: "faq" }))
      return this.cachedResult({ id, mode, key: "faq" });
    const faq = await this.sdk.getFaq({ id }).catch((e) => {
      console.log("Error getting faq", e);
      return {};
    });
    DataStore.memory[mode][id].faq = faq;
  }

  static async getSectionsTags({ sections }: { sections: string[] }) {
    return;
    // if (!DataStore.memory.section) DataStore.memory.section = {};
    // const toGetSections = sections.filter(
    //   (section) => !DataStore.memory.section[section],
    // );
    // const brand = BRAND_OVERRIDE || window.location.host;
    // if (toGetSections.length === 0) return;
    // const tags = await fetch(
    //   `${SDKConfiguration.API_ENDPOINT}/api/tags?link=${brand}`,
    //   {
    //     method: "POST",
    //     body: JSON.stringify({
    //       sections: toGetSections,
    //       products: [],
    //     }),
    //   },
    // ).then((e) => e.json() as Promise<{ sectionData: Record<string, any> }>);

    // DataStore.memory.section = { ...DataStore.memory.section, ...tags };
  }

  static async getSimilar({ id }: { id: string }) {
    if (this.inCache({ id, mode: "product", key: "similar" }))
      return this.cachedResult({ id, mode: "product", key: "similar" });
    const similar = await this.sdk.getSimilar({ id }).catch((e) => {
      console.log("Error getting similar", e);
      return [];
    });
    DataStore.memory.product[id].similar = similar ?? [];
  }
  static async getComplementary({ id }: { id: string }) {
    if (this.inCache({ id, mode: "product", key: "complementary" }))
      return this.cachedResult({ id, mode: "product", key: "complementary" });
    let complementary = await this.sdk.getComplementary({ id }).catch((e) => {
      console.log("Error getting similar", e);
      return {};
    });
    if (Array.isArray(complementary)) {
      complementary = transformComplementary(complementary);
    }

    DataStore.memory.product[id].complementary = complementary;
  }
  static async getDetails({
    id,
    mode = "product",
  }: {
    id: string;
    mode?: MemoryKeys;
  }) {
    if (this.inCache({ id, mode, key: "details" }))
      return this.cachedResult({ id, mode, key: "details" });
    const details = await this.sdk.getProductDetails({ id });
    DataStore.memory.product[id].details = details;
  }
  static async getTagRecommendation({
    id,
    mode = "product",
  }: {
    id: string;
    mode?: MemoryKeys;
  }) {
    try {
      if (this.inCache({ id, mode, key: "tagRecommendation" })) {
        return this.cachedResult({ id, mode, key: "tagRecommendation" });
      }

      const tagRecommendation = await this.sdk.getProductTagRecommendation({
        id,
      });
      if (tagRecommendation) {
        if (!DataStore.memory.product[id]) {
          DataStore.memory.product[id] = {};
        }
        DataStore.memory.product[id].tagRecommendation =
          tagRecommendation ?? {};
      }
      return tagRecommendation;
    } catch (error) {
      console.error("Error in getTagRecommendation:", error);
      return null;
    }
  }
  static async getUiDescription({
    id,
    mode = "product",
  }: {
    id: string;
    mode?: MemoryKeys;
  }) {
    if (this.inCache({ id, mode, key: "uiDescription" }))
      return this.cachedResult({ id, mode, key: "uiDescription" });
    const details = await this.sdk.getProductUiDescription({ id });
    DataStore.memory.product[id].uiDescription = details;
  }
  static async getSubcategoryTags({ id }: { id: string }) {
    if (this.inCache({ id, mode: "subcategory", key: "tags" }))
      return this.cachedResult({ id, mode: "subcategory", key: "tags" });
    const response = await fetch(
      "https://helium-intelligence.s3.eu-north-1.amazonaws.com/global/apparel/tiers.json",
    );
    if (!response.ok) {
      throw new Error("Failed to fetch tiers data");
    }
    const data = await response.json();
    if (!data || !data[id.toLowerCase()]) {
      throw new Error(`No data found for subcategory: ${id}`);
    }
    for (const key of Object.keys(data)) {
      if (key.toLowerCase() === id.toLowerCase()) {
        const existingData = DataStore.memory.subcategory[key] || {};
        DataStore.memory.subcategory[key] = {
          ...data[key],
          keyMappings: existingData.keyMappings || {},
          initialized: existingData.initialized || false,
        };
        return DataStore.memory.subcategory[key];
      }
    }
  }
  static async getProducts() {
    if (!DataStore.memory) {
      DataStore.memory = {};
    }
    if (!DataStore.memory.list) {
      DataStore.memory.list = {};
    }
    if (!DataStore.memory.list.products) {
      DataStore.memory.list.products = {};
    }
    const list = await this.sdk
      .getProducts()
      .catch((e) => console.log("Error getting all products", e));
    DataStore.memory.list.products = list;
  }

  fuseIndex: Fuse<Record<string, any>> | null = null;
  private static async getFuseIndex() {
    if (window.helium?.fuse) return window.helium.fuse;
    try {
      const productIds = await window.helium.datastore.getProducts();
      let index = [];
      const brandData = window.helium.brandData;
      //TODO replace with cloudfront
      const res = await fetch(
        `${cloudfrontUrl}/${brandData.orgId}/products/fuse-index.${brandData.storeId}.json`,
      );
      if (res.ok) {
        index = await res.json();
      }

      const options: any = {
        includeScore: false,
        useExtendedSearch: true,
        shouldSort: true,
        findAllMatches: false,
        minMatchCharLength: 3,
        threshold: 0,
        keys: [
          "title",
          "vendor",
          "tags.visual.colour.primary.value",
          "tags.visual.material",
          "tags.visual.pattern",
          "tags.physical.gender",
          "generatedTags",
          "subcategory",
          "subcategory_type",
        ],
      };

      const fuse = new Fuse(
        productIds.map((val) => ({
          id: val,
        })),
        options,
        Fuse.parseIndex(index),
      );
      window.helium.fuse = fuse;

      return fuse;
    } catch (error) {
      console.error(`Error creating Fuse index for orgID`, error);
      return null;
    }
  }
  static async search({ query, filterKeys, ignoreHandle }: Search) {
    if (!DataStore.memory.filters) {
      DataStore.memory.filters = {};
    }
    if (!DataStore.memory.filters.products) {
      DataStore.memory.filters.products = [];
    }

    try {
      const fuse = await this.getFuseIndex();
      console.log("Fuse Index: ", fuse);
      if (!fuse) {
        console.error(`No Fuse instance found`);
        return [];
      }

      const searchCriteria: any = { $and: [] };

      const path = window.location.pathname;
      const currentPage = window.helium.datastore.current.page;
      const isPDP = currentPage.type === "product";

      if (window.helium.datastore.current.product && isPDP) {
        searchCriteria.$and.push({
          subcategory: DataStore.current.product.tags?.subcategory
            ?.split?.("/")
            ?.at?.(-1),
          //category: DataStore.current.product.tags?.category,
          // "tags.physical.gender":
          // DataStore.current.product.tags?.tags?.physical?.gender,
        });
      }
      if (
        currentPage.type === "collection" &&
        currentPage.filters?.subcategory
      ) {
        searchCriteria.$and.push({
          subcategory: currentPage.filters.subcategory,
        });
      }

      const keyToKeyMapping = {
        primaryColor: "tags.visual.color.primary.value",
        material: "tags.visual.material",
        pattern: "tags.visual.pattern",
        size: "tags.physical.size",
        priceRange: "tags.market.priceRange",
        colour: "tags.physical.colour.primary.value",
        gender: "tags.physical.gender",
      };
      if (filterKeys && Object.keys(filterKeys).length > 0) {
        Object.entries(filterKeys).forEach(([key, values]) => {
          if (Array.isArray(values)) {
            searchCriteria.$and.push({
              $or: values.map((value) => ({ [keyToKeyMapping[key]]: value })),
            });
          } else {
            searchCriteria.$and.push({ [keyToKeyMapping[key]]: values });
          }
        });
      }

      if (query) {
        searchCriteria.$and.push({
          $or: fuse.options.keys.map((key: string) => ({
            [key]: query,
          })),
        });
      }

      if (searchCriteria.$and.length === 0) {
        return fuse.getIndex().docs.map((doc: any) => doc.id);
      }

      const results = fuse.search(searchCriteria, { limit: 10 });
      console.log(
        "Search Criteria: ",
        JSON.stringify(searchCriteria, null, 2),
        { results },
      );

      const searchResult = results.map((val: any) => val.item.id);

      DataStore.memory.filters.products = searchResult.filter(
        (s) => s !== ignoreHandle,
      );
    } catch (error) {
      console.error(`Error searching with Fuse for orgId`, error);
    }
  }
}

export function transformComplementary(
  data: {
    subcategory: string;
    attribute: string;
    attributeValue: string;
    results: string[];
  }[],
) {
  return data;
  const result: any = {};
  console.log({ data }, "lowTicketSize");
  data.forEach((item) => {
    const { subcategory, attribute, attributeValue, results } = item;
    if (!result[subcategory]) {
      result[subcategory] = {};
    }
    if (!result[subcategory][attribute]) {
      result[subcategory][attribute] = [];
    }
    result[subcategory][attribute].push(...results);
  });
  return result;
}
